import React from "react";
import { Icon } from "@iconify/react";
import { useButtonTableStyle } from "../../utils/useButtonTableStyle";
import { formatGirviCurrency } from "./girviTheme";
import { useDashboardValuePrivacy } from "../../hooks/useDashboardValuePrivacy";
import PrivacyBlur from "../../components/PrivacyBlur";

/** ERP may return `{ id, name }` for user/particular fields — never render raw objects in JSX. */
function safeDisplayValue(value, fallback = "") {
  if (value == null || value === "") return fallback;
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }
  if (typeof value === "object") {
    if (value.name != null) return String(value.name);
    if (value.label != null) return String(value.label);
    if (value.id != null) return `#${value.id}`;
  }
  return fallback;
}

const TransactionHistoryTable = ({
  loading = false,
  data = [],
  onViewAll,
  idField = "girvi_no",
  idLabel = "Dihran ID",
}) => {
  const { valuesHidden } = useDashboardValuePrivacy();
  const { tableClass } = useButtonTableStyle();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const displayedTransactions = Array.isArray(data) ? data : [];

  return (
    <div className="girvi-tx-card">
      <div className="girvi-tx-header">
        <div>
          <h2 className="girvi-dash-section-label mb-0">Today's transactions</h2>
          <p className="girvi-tx-count">
            {loading
              ? "Loading…"
              : `${displayedTransactions.length} ${
                  displayedTransactions.length === 1 ? "entry" : "entries"
                }`}
          </p>
        </div>
        {onViewAll && (
          <button type="button" className="girvi-tx-link" onClick={onViewAll}>
            View all
            <Icon icon="solar:arrow-right-linear" width={14} />
          </button>
        )}
      </div>

      {loading ? (
        <div className="girvi-tx-empty">
          <span className="girvi-ledger-skel girvi-ledger-skel--sm" />
        </div>
      ) : displayedTransactions.length === 0 ? (
        <div className="girvi-tx-empty">
          <Icon icon="solar:clipboard-list-linear" width={28} />
          <p>No transactions recorded today</p>
        </div>
      ) : (
        <div className="table-responsive girvi-tx-table-wrap">
          <table className={`table text-nowrap mb-0 align-middle ${tableClass}`}>
            <thead>
              <tr>
                <th>Date</th>
                <th>{idLabel}</th>
                <th>Particulars</th>
                <th className="text-end">Amount</th>
                <th>Action by</th>
              </tr>
            </thead>
            <tbody>
              {displayedTransactions.map((transaction, index) => (
                <tr key={index}>
                  <td>{formatDate(transaction.date)}</td>
                  <td>
                    <span className="girvi-tx-id">
                      {safeDisplayValue(
                        transaction[idField] ?? transaction.girvi_no,
                        "—",
                      )}
                    </span>
                  </td>
                  <td>{safeDisplayValue(transaction.particulars, "—")}</td>
                  <td className="text-end fw-semibold">
                    <PrivacyBlur hidden={valuesHidden}>
                      {formatGirviCurrency(transaction.amount)}
                    </PrivacyBlur>
                  </td>
                  <td>{safeDisplayValue(transaction.by_user, "—")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TransactionHistoryTable;
